import type { AudioChunk } from '../types';

export interface AudioStats {
  /** リングバッファ内のフレーム数 */
  buffered: number;
  capacity: number;
  underruns: number;
  /** 秒 */
  latency: number;
}

const WORKLET_URL = '/worklets/pcm-player.js';
const CHANNELS = 2;

/**
 * Web Audio API 出力。
 *
 * iOS Safari の要点:
 *  - AudioContext はユーザー操作イベントの同期実行中に resume() しないと動かない。
 *    → unlock() を pointerdown ハンドラから必ず呼ぶこと。
 *  - サイレントスイッチ ON でも鳴らすには 'playback' カテゴリ相当の扱いが要る。
 *    HTMLAudio の無音ループを併用すると回避できる（下記 keepAlive）。
 *  - ページがバックグラウンドに回ると suspend される。visibilitychange で復帰させる。
 */
export class AudioOutput {
  private ctx: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private fallback: ScriptProcessorNode | null = null;
  private gain: GainNode | null = null;
  private keepAlive: HTMLAudioElement | null = null;

  private volume = 0.8;
  private muted = false;
  private ready = false;
  private workletFailed = false;

  /** ScriptProcessor フォールバック用リングバッファ */
  private fbRing: Float32Array[] = [];
  private fbCapacity = 0;
  private fbRead = 0;
  private fbWrite = 0;
  private fbAvailable = 0;

  private stats: AudioStats = { buffered: 0, capacity: 1, underruns: 0, latency: 0 };

  /** コア側サンプリングレートの記憶（リサンプル用の位相を保持する） */
  private srcRate = 0;
  private resamplePhase = 0;
  private resampleTail = new Float32Array(CHANNELS);
  private hasTail = false;

  get sampleRate(): number {
    return this.ctx?.sampleRate ?? 48000;
  }

  get isReady(): boolean {
    return this.ready;
  }

  get state(): AudioContextState | 'uninitialized' {
    return this.ctx?.state ?? 'uninitialized';
  }

  /**
   * ユーザー操作ハンドラの中から呼ぶこと。
   * 初回は AudioContext の生成 + Worklet ロード、2 回目以降は resume のみ。
   */
  async unlock(): Promise<void> {
    if (!this.ctx) {
      const Ctor: typeof AudioContext =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor({ latencyHint: 'interactive' });

      this.gain = this.ctx.createGain();
      this.gain.gain.value = this.muted ? 0 : this.volume;
      this.gain.connect(this.ctx.destination);

      // iOS: 無音を 1 度鳴らして出力パイプラインを起こす
      const buf = this.ctx.createBuffer(1, 1, this.ctx.sampleRate);
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.ctx.destination);
      src.start(0);
    }

    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }

    if (!this.ready) {
      await this.setupGraph();
      this.ready = true;
    }
  }

  private async setupGraph(): Promise<void> {
    const ctx = this.ctx;
    const gain = this.gain;
    if (!ctx || !gain) return;

    if (!this.workletFailed && ctx.audioWorklet) {
      try {
        await ctx.audioWorklet.addModule(WORKLET_URL);
        const node = new AudioWorkletNode(ctx, 'pcm-player', {
          numberOfInputs: 0,
          numberOfOutputs: 1,
          outputChannelCount: [CHANNELS],
          processorOptions: { channels: CHANNELS, volume: 1 },
        });
        node.port.onmessage = (e: MessageEvent) => {
          const d = e.data as { type: string; buffered: number; capacity: number; underruns: number };
          if (d.type === 'stats') {
            this.stats = {
              buffered: d.buffered,
              capacity: d.capacity,
              underruns: d.underruns,
              latency: d.buffered / ctx.sampleRate,
            };
          }
        };
        node.connect(gain);
        this.node = node;
        return;
      } catch (err) {
        console.warn('[audio] AudioWorklet unavailable, falling back to ScriptProcessor', err);
        this.workletFailed = true;
      }
    }

    this.setupFallback(ctx, gain);
  }

  /** 古い iOS / WebView 向け。非推奨 API だが実機互換のため残す。 */
  private setupFallback(ctx: AudioContext, gain: GainNode): void {
    this.fbCapacity = Math.ceil(ctx.sampleRate * 0.5);
    this.fbRing = [new Float32Array(this.fbCapacity), new Float32Array(this.fbCapacity)];
    this.fbRead = 0;
    this.fbWrite = 0;
    this.fbAvailable = 0;

    const proc = ctx.createScriptProcessor(2048, 0, CHANNELS);
    proc.onaudioprocess = (ev) => {
      const frames = ev.outputBuffer.length;
      const cap = this.fbCapacity;
      for (let c = 0; c < CHANNELS; c++) {
        const out = ev.outputBuffer.getChannelData(c);
        const ring = this.fbRing[c];
        if (!ring) continue;
        let r = this.fbRead;
        for (let i = 0; i < frames; i++) {
          out[i] = i < this.fbAvailable ? (ring[r] ?? 0) : 0;
          if (i < this.fbAvailable) r = r + 1 === cap ? 0 : r + 1;
        }
      }
      const consumed = Math.min(frames, this.fbAvailable);
      this.fbRead = (this.fbRead + consumed) % cap;
      this.fbAvailable -= consumed;
      this.stats = {
        buffered: this.fbAvailable,
        capacity: cap,
        underruns: this.stats.underruns + (consumed < frames ? 1 : 0),
        latency: this.fbAvailable / ctx.sampleRate,
      };
    };
    proc.connect(gain);
    this.fallback = proc;
  }

  /**
   * コアが吐いた PCM を投入する。
   * サンプリングレートが AudioContext と異なる場合は線形補間でリサンプルする。
   */
  push(chunk: AudioChunk): void {
    if (!this.ready || !this.ctx) return;
    const dstRate = this.ctx.sampleRate;
    const frames = chunk.samples.length >> 1;
    if (frames === 0) return;

    if (this.srcRate !== chunk.sampleRate) {
      this.srcRate = chunk.sampleRate;
      this.resamplePhase = 0;
      this.hasTail = false;
    }

    const float =
      chunk.sampleRate === dstRate
        ? int16ToFloat(chunk.samples)
        : this.resample(chunk.samples, chunk.sampleRate, dstRate);

    const outFrames = float.length >> 1;
    if (outFrames === 0) return;

    if (this.node) {
      this.node.port.postMessage({ type: 'samples', data: float, frames: outFrames }, [float.buffer]);
    } else if (this.fallback) {
      this.pushFallback(float, outFrames);
    }
  }

  private pushFallback(data: Float32Array, frames: number): void {
    const cap = this.fbCapacity;
    if (this.fbAvailable + frames > cap) {
      const drop = this.fbAvailable + frames - cap;
      this.fbRead = (this.fbRead + drop) % cap;
      this.fbAvailable -= drop;
    }
    let w = this.fbWrite;
    for (let i = 0; i < frames; i++) {
      for (let c = 0; c < CHANNELS; c++) {
        const ring = this.fbRing[c];
        if (ring) ring[w] = data[i * CHANNELS + c] ?? 0;
      }
      w = w + 1 === cap ? 0 : w + 1;
    }
    this.fbWrite = w;
    this.fbAvailable += frames;
  }

  /**
   * 線形補間リサンプラ。チャンク境界で位相と直前サンプルを保持するので
   * つなぎ目でプチノイズが出ない。
   */
  private resample(src: Int16Array, srcRate: number, dstRate: number): Float32Array {
    const ratio = srcRate / dstRate;
    const inFrames = src.length >> 1;
    const outFrames = Math.floor((inFrames - this.resamplePhase) / ratio) + 1;
    const out = new Float32Array(Math.max(0, outFrames) * CHANNELS);

    let pos = this.resamplePhase;
    let o = 0;
    while (o < outFrames) {
      const i0 = Math.floor(pos);
      const frac = pos - i0;
      if (i0 + 1 >= inFrames) break;

      for (let c = 0; c < CHANNELS; c++) {
        const a =
          i0 < 0 && this.hasTail
            ? (this.resampleTail[c] ?? 0)
            : (src[i0 * CHANNELS + c] ?? 0) / 32768;
        const b = (src[(i0 + 1) * CHANNELS + c] ?? 0) / 32768;
        out[o * CHANNELS + c] = a + (b - a) * frac;
      }
      o++;
      pos += ratio;
    }

    for (let c = 0; c < CHANNELS; c++) {
      this.resampleTail[c] = (src[(inFrames - 1) * CHANNELS + c] ?? 0) / 32768;
    }
    this.hasTail = true;
    this.resamplePhase = pos - inFrames;

    return o === outFrames ? out : out.subarray(0, o * CHANNELS).slice();
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.gain) {
      // 急激な変化はクリックノイズになるので短いランプをかける
      const now = this.ctx?.currentTime ?? 0;
      this.gain.gain.cancelScheduledValues(now);
      this.gain.gain.setTargetAtTime(this.muted ? 0 : this.volume, now, 0.01);
    }
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.setVolume(this.volume);
  }

  getStats(): AudioStats {
    return this.stats;
  }

  flush(): void {
    this.node?.port.postMessage({ type: 'reset' });
    this.fbRead = 0;
    this.fbWrite = 0;
    this.fbAvailable = 0;
    this.hasTail = false;
    this.resamplePhase = 0;
  }

  async suspend(): Promise<void> {
    if (this.ctx && this.ctx.state === 'running') await this.ctx.suspend();
  }

  async resume(): Promise<void> {
    if (this.ctx && this.ctx.state === 'suspended') await this.ctx.resume();
  }

  dispose(): void {
    this.node?.disconnect();
    this.fallback?.disconnect();
    if (this.fallback) this.fallback.onaudioprocess = null;
    this.gain?.disconnect();
    this.keepAlive?.pause();
    void this.ctx?.close();
    this.ctx = null;
    this.node = null;
    this.fallback = null;
    this.gain = null;
    this.ready = false;
  }
}

function int16ToFloat(src: Int16Array): Float32Array {
  const out = new Float32Array(src.length);
  for (let i = 0; i < src.length; i++) out[i] = (src[i] ?? 0) / 32768;
  return out;
}
